import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { ToastModule } from "primeng/toast";
import { DialogModule } from "primeng/dialog";
import { InputTextareaModule } from "primeng/inputtextarea";
import { DropdownModule } from "primeng/dropdown";
import { InputTextModule } from "primeng/inputtext";
import { FormsModule } from "@angular/forms";
import { TagModule } from "primeng/tag";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { MessageService } from "primeng/api";
import { forkJoin } from "rxjs";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";
import { PermissionEnum } from "../../model/permission-enum";
import { CourtService } from "../../services/court.service";
import { UserService } from "../../services/user.service";
import { ClubService } from "../../services/club.service";
import { PlayerService } from "../../services/player.service";
import { CustomNotificationService } from "../../services/custom-notification.service";
import { LocalStorageService } from "../../services/local-storage.service";
import { User } from "../../model/user";

@Component({
  selector: "app-approve-matches",
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    TableModule,
    ButtonModule,
    ToastModule,
    DialogModule,
    InputTextareaModule,
    DropdownModule,
    InputTextModule,
    FormsModule,
    TagModule,
    ProgressSpinnerModule,
  ],
  providers: [MessageService],
  templateUrl: "./approve-matches.component.html",
  styleUrl: "./approve-matches.component.css",
})
export class ApproveMatchesComponent implements OnInit {
  reservations: any[] = [];
  loading = false;
  accessDenied = false;
  clubId: number | null = null;
  clubName = "";
  courtMap: { [key: number]: string } = {};
  bookerPhoneMap: { [key: number]: string } = {};
  vipIds: Set<number> = new Set<number>();
  vipNotes: { [key: number]: string } = {};
  denyDialogVisible = false;
  denyReason = "";
  reservationToDeny: any | null = null;
  approveDialogVisible = false;
  reservationToApprove: any | null = null;
  actionLoading = false;

  endDialogVisible = false;
  reservationToEnd: any | null = null;
  confirmArrivalDialogVisible = false;

  matchInfoDialogVisible = false;
  matchInfo: any | null = null;

  showMatchInfoColumn = false;
  currentUser?: User;
  users: User[] = [];

  towels = 0;
  balls = 0;
  rackets = 0;
  paymentType: "cash" | "card" = "cash";
  noteText = "";
  noScoreRecorded = false;

  sets: number[][] = [
    [0, 0],
    [0, 0],
    [0, 0],
  ];
  scoreOptions = [0, 1, 2, 3, 4, 5, 6, 7];

  numberOptions = [0, 1, 2, 3, 4, 5];
  paymentOptions = [
    { label: "Cash", value: "cash" },
    { label: "Card", value: "card" },
  ];

  constructor(
    private reservationService: CourtReservationService,
    private authService: AuthenticationService,
    private courtService: CourtService,
    private userService: UserService,
    private messageService: MessageService,
    private clubService: ClubService,
    private playerService: PlayerService,
    private notificationService: CustomNotificationService,
    private localStorageService: LocalStorageService,
  ) {}

  async ngOnInit(): Promise<void> {
    const current = this.authService.currentUserValue;
    this.currentUser = current || undefined;
    this.users = (await this.localStorageService.getItem("users")) || [];
    let permissions = this.authService.currentUserPermissions;
    let hasAdminPermission = permissions.some(
      (permission: PermissionEnum) => permission == PermissionEnum.Admin,
    );
    if (!current || !hasAdminPermission) {
      this.accessDenied = true;
      return;
    }

    this.userService.getUser(current.id).subscribe({
      next: (u) => {
        this.clubId = u.club?.id ?? u.club_id;
        this.clubName = u.club?.name ?? "";
        if (this.clubId) {
          this.loadCourts();
          this.loadVipUsers();
          this.loadReservations();
          this.startAutoRefresh();
        }
      },
      error: () => {
        this.accessDenied = true;
      },
    });
  }

  private startAutoRefresh(): void {
    setInterval(() => {
      if (!this.actionLoading && this.clubId) {
        console.log("Auto-refreshing reservations...");
        this.loadReservations();
      }
    }, 30000);
  }

  loadCourts(): void {
    if (!this.clubId) return;
    this.courtService.getCourtsForClub(this.clubId).subscribe((courts) => {
      this.courtMap = {};
      courts.forEach((c: any) => (this.courtMap[c.id] = c.name));
    });
  }

  loadReservations(): void {
    if (!this.clubId) return;
    this.loading = true;
    const start = new Date(Date.now() - 2 * 60 * 60 * 1000).toDateString();
    const end = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toDateString();
    this.reservationService
      .getReservationsForClubDateRange(this.clubId, start, end, "Approved")
      .subscribe({
        next: (res: any[]) => {
          this.reservations = res.filter(
            (r) => !r.finished && (r.started || this.isWithinArrivalWindow(r)),
          );
          this.showMatchInfoColumn = this.reservations.some(
            (r) => r.end_match_request_pending,
          );
          this.loadUserDetails(() => {
            this.loading = false;
          });
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to load reservations",
          });
        },
      });
  }

  private isWithinArrivalWindow(res: any): boolean {
    const startTime = new Date(res.reservation_start_time).getTime();
    const now = Date.now();
    const thirtyMinBefore = startTime - 30 * 60 * 1000;
    const twoHoursAfter = startTime + 2 * 60 * 60 * 1000;
    return now >= thirtyMinBefore && now <= twoHoursAfter;
  }

  getMatchStatus(res: any): string {
    if (!res.started) return "waiting";
    if (res.end_match_request_pending) return "pending_end";
    if (res.admin_approve_start) return "in_progress";
    return "started";
  }

  getMatchStatusLabel(res: any): string {
    const status = this.getMatchStatus(res);
    switch (status) {
      case "waiting":
        return "Waiting for Arrival";
      case "started":
        return "Awaiting Approval";
      case "in_progress":
        return "In Progress";
      case "pending_end":
        return "Pending End";
      default:
        return "Unknown";
    }
  }

  getMatchStatusClass(res: any): string {
    return this.getMatchStatus(res);
  }

  canConfirmArrival(res: any): boolean {
    return !res.started && this.isWithinArrivalWindow(res);
  }

  confirmArrival(res: any): void {
    this.reservationToApprove = res;
    const parsed = this.parseNote(res.note || "");
    this.towels = parsed.towels ?? 0;
    this.balls = parsed.balls ?? 0;
    this.rackets = parsed.rackets ?? 0;
    this.paymentType = parsed.payment ?? "cash";
    this.noteText = parsed.note ?? "";
    this.confirmArrivalDialogVisible = true;
  }

  submitConfirmArrival(): void {
    if (!this.reservationToApprove) {
      console.error("No reservation to confirm arrival");
      return;
    }

    if (this.actionLoading) {
      console.warn("Action already in progress");
      return;
    }

    const reservationId = this.reservationToApprove.id;
    console.log(`Confirming arrival for reservation ${reservationId}`);

    const note =
      `towels:${this.towels}, ` +
      `balls:${this.balls}, ` +
      `rackets:${this.rackets}, ` +
      `payment:${this.paymentType}, ` +
      `note:${this.noteText}`;

    const updateData = {
      ...this.reservationToApprove,
      started: true,
      admin_approve_start: true,
      note: note,
    };

    console.log("Sending confirm arrival data:", updateData);
    this.actionLoading = true;

    this.reservationService.updateCourtReservation(updateData).subscribe({
      next: (response) => {
        console.log(
          `Successfully confirmed arrival for ${reservationId}:`,
          response,
        );
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: `Player arrival confirmed for reservation ${reservationId}`,
        });
        this.confirmArrivalDialogVisible = false;
        this.loadReservations();
        this.actionLoading = false;
      },
      error: (error) => {
        console.error(`Failed to confirm arrival for ${reservationId}:`, error);
        const errorMessage =
          error?.error?.message || error?.message || "Unknown error";
        this.messageService.add({
          severity: "error",
          summary: "Failed",
          detail: `Failed to confirm arrival: ${errorMessage}`,
        });
        this.actionLoading = false;
      },
    });
  }

  private getBookerId(res: any): number | null {
    const players =
      res.courtReservationPlayer?.map((p: any) => p.player_id) || [];
    if (players.includes(res.user_id)) {
      return res.user_id || null;
    }
    return (
      res.courtReservationPlayer?.find((p: any) => p.position === 0)
        ?.player_id || null
    );
  }

  private loadUserDetails(done: () => void): void {
    const pairs = this.reservations.map((r) => ({
      res: r,
      uid: this.getBookerId(r),
    }));
    const ids = new Set<number>();
    pairs.forEach((p) => {
      if (!p.res.user && p.res.user_id) ids.add(p.res.user_id);
      if (p.uid) ids.add(p.uid);
    });
    const idArray = Array.from(ids);
    if (!idArray.length) {
      done();
      return;
    }
    forkJoin(idArray.map((id) => this.userService.getUser(id))).subscribe({
      next: (users) => {
        const map: { [key: number]: any } = {};
        users.forEach((u) => (map[u.id] = u));
        this.reservations.forEach((r) => {
          if (!r.user && map[r.user_id]) {
            r.user = map[r.user_id];
          }
          const bid = this.getBookerId(r);
          const u = bid ? map[bid] : null;
          if (u) {
            this.bookerPhoneMap[r.id] =
              u.phone_number || u.phone || u.phoneNumber || "";
          }
        });
        done();
      },
      error: () => {
        done();
      },
    });
  }

  loadVipUsers(): void {
    if (!this.clubId) return;
    this.clubService.getVipUsers(this.clubId).subscribe((vip) => {
      this.vipIds = new Set(vip.map((v: any) => v.id));
      this.vipNotes = {};
      vip.forEach(
        (v: any) => (this.vipNotes[v.id] = v.verified_as_vip_note || ""),
      );
    });
  }

  isVip(res: any): boolean {
    const id = res.user?.id || res.user_id;
    return this.vipIds.has(id);
  }

  getVipNote(res: any): string {
    const id = res.user?.id || res.user_id;
    return this.vipNotes[id] || "";
  }

  private parseNote(note: string): {
    towels?: number;
    balls?: number;
    rackets?: number;
    payment?: "cash" | "card";
    note?: string;
  } {
    const pattern =
      /towels:(\d+),\s*balls:(\d+),\s*rackets:(\d+),\s*payment:(cash|card),\s*note:(.*)/i;
    const match = note?.match(pattern);
    if (!match) {
      return {};
    }
    return {
      towels: parseInt(match[1], 10),
      balls: parseInt(match[2], 10),
      rackets: parseInt(match[3], 10),
      payment: match[4] as "cash" | "card",
      note: match[5] || "",
    };
  }

  approveReservation(res: any): void {
    this.reservationToApprove = res;
    const parsed = this.parseNote(res.note || "");
    this.towels = parsed.towels ?? 0;
    this.balls = parsed.balls ?? 0;
    this.rackets = parsed.rackets ?? 0;
    this.paymentType = parsed.payment ?? "cash";
    this.noteText = parsed.note ?? "";
    this.approveDialogVisible = true;
  }

  submitApprove(): void {
    if (!this.reservationToApprove) {
      console.error("No reservation to approve");
      return;
    }

    if (this.actionLoading) {
      console.warn("Action already in progress, ignoring duplicate request");
      return;
    }

    const reservationId = this.reservationToApprove.id;
    console.log(`Starting approval process for reservation ${reservationId}`);

    const note =
      `towels:${this.towels}, ` +
      `balls:${this.balls}, ` +
      `rackets:${this.rackets}, ` +
      `payment:${this.paymentType}, ` +
      `note:${this.noteText}`;

    const updateData = {
      ...this.reservationToApprove,
      admin_approve_start: true,
      note: note,
    };

    console.log("Sending approval data:", updateData);
    this.actionLoading = true;

    this.reservationService.updateCourtReservation(updateData).subscribe({
      next: (response) => {
        console.log(
          `Successfully approved reservation ${reservationId}:`,
          response,
        );
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: `Match ${reservationId} approved successfully`,
        });
        this.approveDialogVisible = false;
        this.loadReservations();
        this.actionLoading = false;
      },
      error: (error) => {
        console.error(`Failed to approve reservation ${reservationId}:`, error);
        const errorMessage =
          error?.error?.message || error?.message || "Unknown error occurred";
        this.messageService.add({
          severity: "error",
          summary: "Approval Failed",
          detail: `Failed to approve match ${reservationId}: ${errorMessage}`,
        });
        this.actionLoading = false;

        setTimeout(() => {
          console.log("Retrying approval after error...");
          this.retryApproval(updateData, reservationId);
        }, 2000);
      },
    });
  }

  openDenyDialog(res: any): void {
    this.reservationToDeny = res;
    this.denyReason = "";
    this.denyDialogVisible = true;
  }

  submitDeny(): void {
    if (!this.reservationToDeny) return;
    this.actionLoading = true;
    this.reservationService
      .updateCourtReservation({
        ...this.reservationToDeny,
        started: false,
        admin_approve_start: false,
        deny_reason: this.denyReason,
      })
      .subscribe({
        next: () => {
          const id = this.reservationToDeny?.id;
          if (id) {
            this.reservationService.deleteCourtReservation(id).subscribe({
              next: () => {
                this.messageService.add({
                  severity: "success",
                  summary: "Success",
                  detail: "Match denied",
                });
                this.denyDialogVisible = false;
                this.loadReservations();
                this.actionLoading = false;
              },
              error: () => {
                this.messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail: "Action failed",
                });
                this.actionLoading = false;
              },
            });
          } else {
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: "Match denied",
            });
            this.denyDialogVisible = false;
            this.loadReservations();
            this.actionLoading = false;
          }
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Action failed",
          });
          this.actionLoading = false;
        },
      });
  }

  viewMatchInfo(res: any): void {
    this.matchInfo = res;
    this.matchInfoDialogVisible = true;
  }

  endMatch(res: any): void {
    this.reservationToEnd = res;
    const parsed = this.parseNote(res.note || "");
    this.towels = parsed.towels ?? 0;
    this.balls = parsed.balls ?? 0;
    this.rackets = parsed.rackets ?? 0;
    this.paymentType = parsed.payment ?? "cash";
    this.noteText = parsed.note ?? "";
    this.noScoreRecorded = this.isPhoneBooking(res);
    if (res.sets && res.sets.length >= 6) {
      this.sets = [
        [res.sets[0] ?? 0, res.sets[3] ?? 0],
        [res.sets[1] ?? 0, res.sets[4] ?? 0],
        [res.sets[2] ?? 0, res.sets[5] ?? 0],
      ];
    } else {
      this.sets = [
        [0, 0],
        [0, 0],
        [0, 0],
      ];
    }
    this.endDialogVisible = true;
  }

  isPhoneBooking(res: any): boolean {
    const reservationType = (res?.reservation_type || "")
      .toString()
      .toLowerCase();
    const phoneCallPlayers = res?.courtReservationPhoneCallPlayer || [];
    const playerIds =
      res?.player_ids ||
      res?.courtReservationPlayer?.map((p: any) => p.player_id) ||
      [];
    const registeredPlayerIds = playerIds.filter((id: any) => !!id);

    return (
      reservationType === "basic" ||
      phoneCallPlayers.length > 0 ||
      registeredPlayerIds.length === 0
    );
  }

  private getSetsArray(): number[] {
    return [
      this.sets[0][0],
      this.sets[1][0],
      this.sets[2][0],
      this.sets[0][1],
      this.sets[1][1],
      this.sets[2][1],
    ];
  }

  private getNoScoreSetsArray(): number[] {
    return [-1, -1, -1, -1, -1, -1];
  }

  private scoresComplete(): boolean {
    let team1Sets = 0;
    let team2Sets = 0;
    for (let i = 0; i < 3; i++) {
      if (this.sets[i][0] > this.sets[i][1]) team1Sets++;
      else if (this.sets[i][1] > this.sets[i][0]) team2Sets++;
    }
    return team1Sets >= 2 || team2Sets >= 2;
  }

  submitEnd(): void {
    if (!this.reservationToEnd) {
      console.error("No reservation to end");
      return;
    }

    if (this.actionLoading) {
      console.warn("Action already in progress, ignoring duplicate request");
      return;
    }

    const reservationId = this.reservationToEnd.id;
    const isPhone = this.isPhoneBooking(this.reservationToEnd);
    const isNoScore = isPhone || this.noScoreRecorded;
    console.log(
      `Starting end match process for reservation ${reservationId}, isPhoneBooking: ${isPhone}, noScore: ${isNoScore}`,
    );

    if (!isNoScore && !this.scoresComplete()) {
      this.messageService.add({
        severity: "warning",
        summary: "Incomplete Match",
        detail: "Please enter valid scores. One team must win at least 2 sets.",
      });
      return;
    }

    const note =
      `towels:${this.towels}, ` +
      `balls:${this.balls}, ` +
      `rackets:${this.rackets}, ` +
      `payment:${this.paymentType}, ` +
      `note:${this.noteText}`;

    const setsArray = isNoScore
      ? this.getNoScoreSetsArray()
      : this.getSetsArray();

    const updateData = {
      ...this.reservationToEnd,
      admin_approve_start: true,
      finished: true,
      end_match_request_pending: false,
      note: note,
      sets: setsArray,
    };

    console.log("Sending end match data:", updateData);
    this.actionLoading = true;

    this.reservationService.updateCourtReservation(updateData).subscribe({
      next: (response) => {
        console.log(
          `Successfully ended reservation ${reservationId}:`,
          response,
        );
        if (!isNoScore) {
          this.awardPointsForMatch(updateData);
        }
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: `Match ${reservationId} ended successfully`,
        });
        this.endDialogVisible = false;
        this.loadReservations();
        this.actionLoading = false;
      },
      error: (error) => {
        console.error(`Failed to end reservation ${reservationId}:`, error);
        const errorMessage =
          error?.error?.message || error?.message || "Unknown error occurred";
        this.messageService.add({
          severity: "error",
          summary: "End Match Failed",
          detail: `Failed to end match ${reservationId}: ${errorMessage}`,
        });
        this.actionLoading = false;

        setTimeout(() => {
          console.log("Retrying end match after error...");
          this.retryEndMatch(updateData, reservationId);
        }, 2000);
      },
    });
  }

  getUserName(res: any): string {
    if (!res.user) return "";
    if (res.user.full_name) return res.user.full_name;
    const parts: string[] = [];
    if (res.user.name) parts.push(res.user.name);
    if (res.user.last_name) parts.push(res.user.last_name);
    if (parts.length) return parts.join(" ");
    return res.user.username || "";
  }

  getBookerPhone(res: any): string {
    return this.bookerPhoneMap[res.id] || "";
  }

  private calculatePoints(
    team: number,
    wins: number[],
    winnerTeam: number,
  ): number {
    const setsWon = wins[team];
    const setsLost = wins[team ^ 1];
    let points = 15 + setsWon * 20 - setsLost * 5;
    if (winnerTeam === team) points += 20;
    return points;
  }

  private awardPointsForMatch(reservation: any): void {
    if (!reservation.sets || reservation.sets.some((s: number) => s === -1)) {
      console.log("Match sets not complete, skipping points calculation");
      return;
    }

    const sets = reservation.sets;
    const wins = [0, 0];
    for (let i = 0; i < 3; i++) {
      const t0 = sets[i];
      const t1 = sets[i + 3];
      if (t0 > t1) wins[0]++;
      else if (t1 > t0) wins[1]++;
    }

    if (Math.max(wins[0], wins[1]) < 2) {
      console.log("Match not won by any team (need 2 sets), skipping points");
      return;
    }

    const winnerTeam = wins[0] > wins[1] ? 0 : 1;
    const playerIds =
      reservation.courtReservationPlayer?.map((p: any) => p.player_id) || [];

    console.log(
      `Awarding points for match ${reservation.id}. Winner team: ${winnerTeam}, Sets: ${wins[0]}-${wins[1]}`,
    );

    let successfulUpdates = 0;
    let totalUpdates = playerIds.length;
    let hasErrors = false;

    for (let i = 0; i < playerIds.length; i++) {
      const id = playerIds[i];
      const team = i % 2;
      const setsWon = wins[team];
      const setsLost = wins[team ^ 1];
      let points = 15 + setsWon * 20 - setsLost * 5;
      if (winnerTeam === team) points += 20;

      const winsChange = winnerTeam === team ? 1 : 0;
      const losesChange = winnerTeam === team ? 0 : 1;

      console.log(
        `Updating stats for player ${id}: points=${points}, wins=${winsChange}, loses=${losesChange}`,
      );

      this.playerService
        .updatePlayerStats({
          id,
          pp_point_change: points,
          wins_change: winsChange,
          loses_change: losesChange,
          point_change: 0,
        })
        .subscribe({
          next: (response) => {
            console.log(
              `Successfully updated stats for player ${id}:`,
              response,
            );
            successfulUpdates++;

            if (id === this.currentUser?.id && this.currentUser) {
              const updated = {
                ...this.currentUser,
                pp_points: (this.currentUser.pp_points || 0) + points,
                wins: (this.currentUser.wins || 0) + winsChange,
                loses: (this.currentUser.loses || 0) + losesChange,
              } as User;
              this.localStorageService.setItem("currentUser", updated);
              this.currentUser = updated;
              this.notificationService.success(
                "Points Updated",
                `You earned ${points} PP points!`,
              );
            }

            for (let j = 0; j < this.users.length; j++) {
              if (this.users[j].id === id) {
                this.users[j] = {
                  ...this.users[j],
                  pp_points: (this.users[j].pp_points || 0) + points,
                  wins: (this.users[j].wins || 0) + winsChange,
                  loses: (this.users[j].loses || 0) + losesChange,
                } as User;
                this.localStorageService.setItem("users", this.users);
                break;
              }
            }

            if (successfulUpdates === totalUpdates && !hasErrors) {
              this.messageService.add({
                severity: "success",
                summary: "Points Awarded",
                detail:
                  "Match points have been calculated and awarded to all players",
              });
            }
          },
          error: (error) => {
            console.error(`Failed to update stats for player ${id}:`, error);
            hasErrors = true;
            this.messageService.add({
              severity: "error",
              summary: "Points Update Failed",
              detail: `Failed to update points for player ${id}. Please contact support.`,
            });
            this.notificationService.error(
              "Points Update Failed",
              `Failed to update points for player ${id}. Please contact support.`,
            );
          },
        });
    }
  }

  private retryApproval(updateData: any, reservationId: number): void {
    console.log(`Retrying approval for reservation ${reservationId}`);
    this.actionLoading = true;

    this.reservationService.updateCourtReservation(updateData).subscribe({
      next: (response) => {
        console.log(
          `Retry successful for reservation ${reservationId}:`,
          response,
        );
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: `Match ${reservationId} approved successfully (retry)`,
        });
        this.approveDialogVisible = false;
        this.loadReservations();
        this.actionLoading = false;
      },
      error: (error) => {
        console.error(`Retry failed for reservation ${reservationId}:`, error);
        this.messageService.add({
          severity: "error",
          summary: "Retry Failed",
          detail: `Failed to approve match ${reservationId} after retry. Please try again manually.`,
        });
        this.actionLoading = false;
      },
    });
  }

  private retryEndMatch(updateData: any, reservationId: number): void {
    console.log(`Retrying end match for reservation ${reservationId}`);
    this.actionLoading = true;

    this.reservationService.updateCourtReservation(updateData).subscribe({
      next: (response) => {
        console.log(
          `Retry successful for ending reservation ${reservationId}:`,
          response,
        );
        if (!updateData.sets?.some((s: number) => s === -1)) {
          this.awardPointsForMatch(updateData);
        }
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: `Match ${reservationId} ended successfully (retry)`,
        });
        this.endDialogVisible = false;
        this.loadReservations();
        this.actionLoading = false;
      },
      error: (error) => {
        console.error(
          `Retry failed for ending reservation ${reservationId}:`,
          error,
        );
        this.messageService.add({
          severity: "error",
          summary: "Retry Failed",
          detail: `Failed to end match ${reservationId} after retry. Please try again manually.`,
        });
        this.actionLoading = false;
      },
    });
  }
}
